import { useRef, useCallback, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PdfRendererState {
  pdfDoc: pdfjsLib.PDFDocumentProxy | null;
  currentFile: string | null;
}

interface RenderOptions {
  zoom?: number;
  maxCanvasSide?: number;
  /** Stable container width (e.g. scroll container). Avoids reading canvas parent which changes with zoom. */
  containerWidth?: number;
}

export function usePdfRenderer() {
  const stateRef = useRef<PdfRendererState>({
    pdfDoc: null,
    currentFile: null,
  });
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  // Load a PDF file (reuse if same file)
  const loadPdf = useCallback(async (url: string): Promise<pdfjsLib.PDFDocumentProxy | null> => {
    if (stateRef.current.currentFile === url && stateRef.current.pdfDoc) {
      return stateRef.current.pdfDoc;
    }
    try {
      const doc = await pdfjsLib.getDocument(url).promise;
      stateRef.current.pdfDoc = doc;
      stateRef.current.currentFile = url;
      return doc;
    } catch (err) {
      console.error('Failed to load PDF:', err);
      return null;
    }
  }, []);

  // Render a specific page to a canvas
  const renderPage = useCallback(
    async (
      canvas: HTMLCanvasElement,
      url: string,
      pageNum: number,
      opts: RenderOptions = {},
    ): Promise<{ width: number; height: number; scale: number } | null> => {
      const doc = await loadPdf(url);
      if (!doc) return null;

      // Cancel previous render
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
      }

      try {
        const page = await doc.getPage(pageNum);
        const zoom = opts.zoom || 1;
        const pixelRatio = window.devicePixelRatio || 1;
        const maxSide = opts.maxCanvasSide || 16384;

        // Base viewport (fit container)
        const baseViewport = page.getViewport({ scale: 1 });

        // Calculate scale to fit canvas container width
        // Use explicit containerWidth to avoid reading canvas parent (which changes with zoom)
        const containerWidth = opts.containerWidth || canvas.parentElement?.clientWidth || 800;
        const baseScale = containerWidth / baseViewport.width;
        const effectiveScale = baseScale * zoom * pixelRatio;

        // Clamp canvas dimensions
        let canvasW = Math.round(baseViewport.width * baseScale * zoom * pixelRatio);
        let canvasH = Math.round(baseViewport.height * baseScale * zoom * pixelRatio);
        let renderScale = effectiveScale;

        if (canvasW > maxSide || canvasH > maxSide) {
          const factor = maxSide / Math.max(canvasW, canvasH);
          canvasW = Math.round(canvasW * factor);
          canvasH = Math.round(canvasH * factor);
          renderScale = effectiveScale * factor;
        }

        canvas.width = canvasW;
        canvas.height = canvasH;

        // CSS dimensions (actual display size)
        const cssW = baseViewport.width * baseScale * zoom;
        const cssH = baseViewport.height * baseScale * zoom;
        canvas.style.width = `${cssW}px`;
        canvas.style.height = `${cssH}px`;

        const viewport = page.getViewport({ scale: renderScale });
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, canvasW, canvasH);

        const renderTask = page.render({
          canvasContext: ctx,
          viewport,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        renderTaskRef.current = null;

        return {
          width: cssW,
          height: cssH,
          scale: baseScale,
        };
      } catch (err: unknown) {
        if (err instanceof Error && err.message?.includes('Rendering cancelled')) {
          return null;
        }
        console.error('Failed to render page:', err);
        return null;
      }
    },
    [loadPdf],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
      }
    };
  }, []);

  return { loadPdf, renderPage };
}
